/**
 * Judge access: link tokens and PINs.
 *
 * Pure on purpose: no Firebase, no React, no ESM-only packages, so Jest can
 * import it directly. The Firestore side lives in utils/firebase.js.
 *
 * Randomness comes only from `crypto.getRandomValues`. Never Math.random:
 * these values gate who can score a bout.
 */

// 32 symbols: lowercase letters and digits minus the lookalikes 0/o, 1/l.
export const JUDGE_TOKEN_ALPHABET = "abcdefghijkmnpqrstuvwxyz23456789";
export const JUDGE_TOKEN_LENGTH = 12; // 12 x 5 bits = 60 bits of entropy
export const JUDGE_PIN_LENGTH = 4;
// Two PINs on the same event must differ in at least this many positions.
// See generateJudgePin for why.
export const JUDGE_PIN_MIN_DISTANCE = 3;

const PIN_SPACE = 10 ** JUDGE_PIN_LENGTH; // 10,000
// Largest multiple of PIN_SPACE that fits in a Uint16. Draws at or above it
// are thrown away, so `value % PIN_SPACE` stays uniform.
const PIN_DRAW_LIMIT = Math.floor(65536 / PIN_SPACE) * PIN_SPACE; // 60,000
const PIN_RANDOM_ATTEMPTS = 200;

// CRA's ESLint env predates globalThis; it exists in every supported browser
// and in Node, which is what lets Jest supply Node's Web Crypto.
/* global globalThis */
const getCrypto = () => {
    const c = typeof globalThis !== "undefined" ? globalThis.crypto : undefined;
    if (!c || typeof c.getRandomValues !== "function") {
        throw new Error(
            "crypto.getRandomValues is unavailable; cannot generate judge credentials."
        );
    }
    return c;
};

/**
 * A fresh link token, e.g. "k7mx2qa9rtwe".
 *
 * Each byte maps to `byte % 32`. 256 is an exact multiple of 32, so every
 * symbol gets exactly 8 of the 256 byte values: no modulo bias, and no need
 * for rejection sampling here.
 */
export function generateJudgeToken() {
    const bytes = new Uint8Array(JUDGE_TOKEN_LENGTH);
    getCrypto().getRandomValues(bytes);
    let token = "";
    for (let i = 0; i < bytes.length; i += 1) {
        token += JUDGE_TOKEN_ALPHABET[bytes[i] % JUDGE_TOKEN_ALPHABET.length];
    }
    return token;
}

/**
 * How many positions two PINs differ in (Hamming distance). A missing
 * position counts as a difference, so "123" vs "1234" is 1.
 */
export function pinDistance(a, b) {
    const x = String(a ?? "");
    const y = String(b ?? "");
    const len = Math.max(x.length, y.length);
    let d = 0;
    for (let i = 0; i < len; i += 1) {
        if (x[i] !== y[i]) d += 1;
    }
    return d;
}

const isPin = (p) => typeof p === "string" && /^\d{4}$/.test(p);

const formatPin = (n) => String(n).padStart(JUDGE_PIN_LENGTH, "0");

const isFarEnough = (candidate, pins) =>
    pins.every((p) => pinDistance(candidate, p) >= JUDGE_PIN_MIN_DISTANCE);

/** One uniform integer in [0, PIN_SPACE), by rejection sampling. */
const randomPinNumber = (c) => {
    const buf = new Uint16Array(1);
    for (;;) {
        c.getRandomValues(buf);
        if (buf[0] < PIN_DRAW_LIMIT) return buf[0] % PIN_SPACE;
    }
};

/** One uniform integer in [0, n), by rejection sampling (n <= 2^32). */
const randomBelow = (c, n) => {
    const buf = new Uint32Array(1);
    const limit = Math.floor(0x100000000 / n) * n;
    for (;;) {
        c.getRandomValues(buf);
        if (buf[0] < limit) return buf[0] % n;
    }
};

/**
 * A 4-digit PIN, "0000" to "9999", that differs from every PIN in
 * `existingPins` in at least 3 of the 4 positions.
 *
 * Why 3: the PIN alone says which judge is scoring. If two judges' PINs were
 * one typo apart, a fat-fingered digit would sign one judge in as the other
 * and their cards would silently mix. Distance 3 means a single wrong digit
 * (or two) can never land on another judge's PIN, and it also rules out
 * transpositions ("1234" vs "1243" differ in only 2 places).
 *
 * Pass every PIN on the event, revoked judges included: they can be restored.
 *
 * Tries random draws first; if those keep colliding (a crowded event), scans
 * all 10,000 PINs and picks uniformly among the ones still allowed. Throws
 * only when no PIN at all satisfies the rule.
 *
 * @param {Array<string>} existingPins
 * @returns {string}
 */
export function generateJudgePin(existingPins = []) {
    const c = getCrypto();
    const pins = (existingPins ?? []).filter(isPin);

    for (let i = 0; i < PIN_RANDOM_ATTEMPTS; i += 1) {
        const candidate = formatPin(randomPinNumber(c));
        if (isFarEnough(candidate, pins)) return candidate;
    }

    const allowed = [];
    for (let n = 0; n < PIN_SPACE; n += 1) {
        const candidate = formatPin(n);
        if (isFarEnough(candidate, pins)) allowed.push(candidate);
    }
    if (allowed.length === 0) {
        throw new Error(
            `No judge PIN left that differs from the event's ${pins.length} existing PINs in at least ${JUDGE_PIN_MIN_DISTANCE} digits.`
        );
    }
    return allowed[randomBelow(c, allowed.length)];
}

/**
 * The first "Judge N" name (N = 1, 2, ...) not already used on the event.
 * @param {Array<string>} existingNames
 */
export function nextJudgeName(existingNames = []) {
    const taken = new Set((existingNames ?? []).map((n) => String(n).trim()));
    let n = 1;
    while (taken.has(`Judge ${n}`)) n += 1;
    return `Judge ${n}`;
}

/**
 * The URL a judge opens, e.g. https://example.com/judge/k7mx2qa9rtwe.
 *
 * @param {string} origin       window.location.origin
 * @param {string} token
 * @param {string} pathTemplate the route pattern; pass JUDGE_PATH from App.jsx
 */
export function buildJudgeUrl(origin, token, pathTemplate = "/judge/:token") {
    const base = String(origin ?? "").replace(/\/+$/, "");
    return `${base}${pathTemplate.replace(
        ":token",
        encodeURIComponent(token ?? "")
    )}`;
}

// The judge page calls in every 10 s while it's on screen, and the server
// writes lastSeenAt at most once a minute, so a judge with the page open is
// never more than about 70 s stale. Past this, they count as away.
export const JUDGE_ONLINE_MS = 2 * 60 * 1000;

const millisOf = (value) => {
    if (!value) return 0;
    if (typeof value.toMillis === "function") return value.toMillis();
    if (value instanceof Date) return value.getTime();
    return typeof value === "number" ? value : 0;
};

/**
 * Where a judge is, for the admin's Judges tab:
 *   "revoked", "not_opened" (never signed in), "online" (the judge page was
 *   on screen in the last 2 minutes) or "away" (signed in, but the page is
 *   closed, in the background or the phone is asleep).
 * `seenAt` is the later of the last sign-in and the last call from the
 * page, in ms, or null.
 *
 * @param {{revokedAt?, lastRedeemedAt?, lastSeenAt?}} judge
 * @param {number} now  Date.now()
 */
export function judgePresence(judge, now) {
    const signedIn = millisOf(judge?.lastRedeemedAt);
    const seenAt = Math.max(signedIn, millisOf(judge?.lastSeenAt)) || null;
    if (judge?.revokedAt) return { state: "revoked", seenAt };
    if (!signedIn) return { state: "not_opened", seenAt: null };
    return { state: now - seenAt <= JUDGE_ONLINE_MS ? "online" : "away", seenAt };
}

/** "just now", "4 min ago", or "at 19:42" once it's an hour or more. */
export function seenAgoText(seenAt, now) {
    if (!seenAt) return "";
    const mins = Math.floor(Math.max(0, now - seenAt) / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins} min ago`;
    const d = new Date(seenAt);
    return `at ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}
